"use client";

import { useSession } from "@saas/auth/hooks/use-session";
import { useRouter } from "@shared/hooks/router";
import { Dialog, DialogContent } from "@ui/components/dialog";
import { useSearchParams } from "next/navigation";
import {
	Suspense,
	createContext,
	useContext,
	useEffect,
	useState,
} from "react";
import type { PropsWithChildren } from "react";
import { LoginForm } from "./LoginForm";

// 创建一个上下文来存储显示登录对话框的状态和方法
type LoginModalContextType = {
	showLoginModal: (redirectTo?: string) => void;
	hideLoginModal: () => void;
};

const LoginModalContext = createContext<LoginModalContextType | undefined>(
	undefined,
);

// 创建一个Hook来使用登录对话框上下文
export function useLoginModal() {
	const context = useContext(LoginModalContext);

	// 如果上下文不存在，提供无操作的默认实现而不是抛出错误
	if (!context) {
		// 如果在客户端环境且不是生产环境，在控制台显示警告
		if (
			typeof window !== "undefined" &&
			process.env.NODE_ENV !== "production"
		) {
			console.warn(
				"useLoginModal was called outside of LoginModalProvider. This will provide a no-op implementation.",
			);
		}

		return {
			showLoginModal: () => {},
			hideLoginModal: () => {},
		};
	}

	return context;
}

// 内部组件包含 useSearchParams 调用
function LoginModalProviderInner({
	children,
	onShowLoginModal,
	onHideLoginModal,
	onRedirectPathChange,
	isOpen,
	redirectPath,
}: PropsWithChildren<{
	onShowLoginModal: (path?: string) => void;
	onHideLoginModal: () => void;
	onRedirectPathChange: (path: string | undefined) => void;
	isOpen: boolean;
	redirectPath: string | undefined;
}>) {
	const { user } = useSession();
	const router = useRouter();
	const searchParams = useSearchParams();

	// 处理show-login事件
	useEffect(() => {
		const handleShowLogin = (event: Event) => {
			const customEvent = event as CustomEvent;
			const path = customEvent.detail?.redirectTo;

			if (path) {
				onRedirectPathChange(path);
				console.log("Login modal with redirect to:", path);
			}

			onShowLoginModal();
		};

		window.addEventListener("show-login", handleShowLogin);

		return () => {
			window.removeEventListener("show-login", handleShowLogin);
		};
	}, [onShowLoginModal, onRedirectPathChange]);

	// 如果用户已登录，则关闭登录对话框
	useEffect(() => {
		if (user && isOpen) {
			onHideLoginModal();

			// 如果有重定向路径，则导航到该路径
			if (redirectPath) {
				console.log("Redirecting after login to:", redirectPath);

				// 使用 setTimeout 确保在 DOM 更新后再进行跳转
				setTimeout(() => {
					router.push(redirectPath);
					onRedirectPathChange(undefined);
				}, 0);
			}
		}
	}, [
		user,
		isOpen,
		redirectPath,
		router,
		onHideLoginModal,
		onRedirectPathChange,
	]);

	// 构建一个URLSearchParams对象来传递重定向路径
	const buildSearchParams = () => {
		const params = new URLSearchParams();
		if (redirectPath) {
			params.set("redirectTo", redirectPath);
		}
		// 保留原始searchParams中的其他参数
		for (const [key, value] of Array.from(searchParams.entries())) {
			if (key !== "redirectTo") {
				params.set(key, value);
			}
		}
		return params;
	};

	return children;
}

export function LoginModalProvider({ children }: PropsWithChildren) {
	const [isOpen, setIsOpen] = useState(false);
	const [redirectPath, setRedirectPath] = useState<string | undefined>(
		undefined,
	);

	const showLoginModal = (path?: string) => {
		if (path) {
			setRedirectPath(path);
		}
		setIsOpen(true);
	};

	const hideLoginModal = () => {
		setIsOpen(false);
		setRedirectPath(undefined);
	};

	return (
		<LoginModalContext.Provider value={{ showLoginModal, hideLoginModal }}>
			<Suspense fallback={children}>
				<LoginModalProviderInner
					onShowLoginModal={() => setIsOpen(true)}
					onHideLoginModal={() => setIsOpen(false)}
					onRedirectPathChange={setRedirectPath}
					isOpen={isOpen}
					redirectPath={redirectPath}
				>
					{children}
				</LoginModalProviderInner>
			</Suspense>

			<Dialog open={isOpen} onOpenChange={setIsOpen}>
				<DialogContent className="sm:max-w-md w-[95%] max-w-md">
					<LoginForm key={redirectPath} redirectTo={redirectPath} />
				</DialogContent>
			</Dialog>
		</LoginModalContext.Provider>
	);
}
